import type { ReactNode } from 'react'
import { Panel } from '@/components/erp/primitives'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import type { ElectionSelection } from './useElection'

/**
 * The election selector, and the gate that stops a screen fetching against a null id.
 *
 * Every screen in this module is scoped to one election. Before this existed, each page
 * hardcoded a mock fixture's uuid and had no selector at all — so a deployment with two
 * elections could only ever look at neither of them.
 */
export function ElectionPicker({ selection }: { selection: ElectionSelection }) {
  const { elections, electionId, select, loading } = selection
  return (
    <label className="flex items-center gap-1.5 text-erp-xs">
      Election
      <select
        className="erp-input h-9"
        value={electionId ?? ''}
        disabled={loading || elections.length === 0}
        onChange={(e) => select(e.target.value)}
      >
        {elections.length === 0 && <option value="">No elections</option>}
        {elections.map((e) => (
          <option key={e.id} value={e.id}>
            {e.name} · {e.statusCode}
          </option>
        ))}
      </select>
    </label>
  )
}

/**
 * Renders `children` only once an election is resolved.
 *
 * The three states it stands in for are all real and all different, and a screen that
 * collapses them into one empty table is a screen nobody can debug: still loading, the
 * deployment has no elections at all, and the election list itself failed to load.
 */
export function ElectionGate({
  selection, children,
}: { selection: ElectionSelection; children: (electionId: string) => ReactNode }) {
  if (selection.error) return <ErrorStrip message={selection.error} />
  if (selection.loading && !selection.electionId) {
    return <LoadingBlock label="Finding the current election…" />
  }
  if (!selection.electionId) {
    return (
      <Panel>
        <Note>
          No election exists yet. Create one from the <strong>Elections</strong> tab —
          cells, pledges and polling-day operations are all scoped to one, so nothing
          else in this module can be set up until there is one.
        </Note>
      </Panel>
    )
  }
  return <>{children(selection.electionId)}</>
}
